/**
 * Saving partner-catalogue lots and writing notes on them (BXS-3627, M19).
 *
 * THE SAME SHORTLIST AND THE SAME NOTES AS OUR OWN AUCTIONS
 * ---------------------------------------------------------
 * Saeed asked, in the 18 August review, to save a horse from the Tattersalls
 * catalogue and write notes on it. Mai's read was that it would be the same
 * feature — and it is, all the way down: the same `wishlists` and
 * `buyer_horse_notes` tables, the same `addToWishlist` / `saveHorseNote`
 * endpoints, and the same two account pages. The only difference on the wire is
 * that these calls send `external_lot_id` where a native one sends
 * `auction_item` / `auction_item_id`.
 *
 * So this service is a thin adapter, not a second implementation. It exists
 * because the International Auctions pages are `OnPush` and need one shared,
 * live view of "is this lot saved, does it have a note" across a catalogue grid,
 * a lot profile and the account page at once.
 *
 * THE THREE RULES
 * ---------------
 *   1. NOTHING HERE RUNS DURING SSR. There is no token on the server, so every
 *      call would be a guaranteed 401 rendered into the page. `isBrowser`
 *      guards each entry point and answers the empty value instead.
 *
 *   2. THE PLATFORM REFUSES WITH HTTP 200. `IsBuyer` and the base controller
 *      both answer `{Error: [...]}` with a 200 status line, so branching on the
 *      status code alone reads a refusal as a success. See `unwrap()`.
 *
 *   3. ONE CACHE, ONE TRUTH. Every write updates a single `states` map and
 *      everything reading it re-renders. A component holding its own copy would
 *      show a lot saved on one page and not on the next.
 */
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { HttpService } from 'src/app/Core/interceptors/http/http.service';
import { BuyerAuthService } from 'src/app/app-modules/buyer/buyer-core/buyer-authentication/services/buyer-auth/buyer-auth.service';
import { environment } from 'src/environments/environment';

import {
  LotNote,
  LotState,
} from '../pages/international-auctions/_mock/international-auctions.models';

/** The buyer API, which is where both features already lived. */
const BUYER_ROOT = `${environment.url}/buyer`;
const NOTES_ROOT = `${BUYER_ROOT}/horse-notes`;

/** What the toggles and `saveNote` answer with. */
export interface LotMutationResult {
  ok: boolean;
  state: LotState | null;
  note?: LotNote | null;
}

@Injectable({ providedIn: 'root' })
export class InternationalMyListService {
  private readonly http = inject(HttpService);
  private readonly auth = inject(BuyerAuthService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Every lot state this session has learned, keyed by external lot id. */
  private readonly statesSubject = new BehaviorSubject<Map<number, LotState>>(
    new Map()
  );
  readonly states$ = this.statesSubject.asObservable();

  /**
   * Lot ids already asked about, so paging back to page 1 does not re-ask.
   * Cleared on sign-out: the next buyer must not inherit this one's answers.
   */
  private readonly asked = new Set<number>();

  constructor() {
    if (this.isBrowser) {
      this.auth.loggedIn_authorized$.subscribe((signedIn) => {
        if (!signedIn) {
          this.reset();
        }
      });
    }
  }

  /** True when these endpoints will answer at all. */
  get isEnabled(): boolean {
    return this.isBrowser && this.auth.isLoggedInAndAuthorized();
  }

  /** The state of one lot as currently known, without asking. */
  stateOf(lotId: number): LotState | null {
    return this.statesSubject.value.get(lotId) ?? null;
  }

  /**
   * Learn the state of a page of lots in one request.
   *
   * Ids already asked about are dropped, so turning back to a page costs
   * nothing.
   */
  loadStates(lotIds: number[]): Observable<boolean> {
    if (!this.isEnabled) {
      return of(false);
    }

    const wanted = lotIds.filter(
      (id) => Number.isFinite(id) && !this.asked.has(id)
    );

    if (wanted.length === 0) {
      return of(false);
    }

    wanted.forEach((id) => this.asked.add(id));

    return this.http
      .post(`${BUYER_ROOT}/externalLotStates`, { external_lot_ids: wanted })
      .pipe(
        map((response) => this.unwrap(response)),
        tap((bag) => {
          const states = (bag?.['states'] ?? []) as Array<
            Record<string, unknown>
          >;
          if (Array.isArray(states)) {
            this.merge(states.map((row) => this.toState(row)));
          }
        }),
        map((bag) => bag !== null),
        catchError(() => {
          // Let a failed read be re-asked. A network blip must not leave these
          // lots permanently unbadged for the rest of the session.
          wanted.forEach((id) => this.asked.delete(id));
          return of(false);
        })
      );
  }

  /**
   * Save or unsave a lot, through the shortlist endpoints our own auctions use.
   *
   * The caller passes the state it WANTS, not a toggle, so a double-click
   * cannot race itself into the opposite of what the buyer sees.
   */
  setFavourite(lotId: number, favourite: boolean): Observable<LotMutationResult> {
    if (!this.isEnabled) {
      return of({ ok: false, state: null });
    }

    const url = favourite
      ? `${BUYER_ROOT}/addToWishlist`
      : `${BUYER_ROOT}/deleteFromWishlist`;

    // Optimistic: the star fills under the finger, not a round trip later.
    // Rolled back below if the server disagrees.
    const previous = this.stateOf(lotId);
    this.merge([
      {
        lot_id: lotId,
        is_favourite: favourite,
        has_note: previous?.has_note ?? false,
        rating: previous?.rating ?? null,
      },
    ]);

    return this.http.post(url, { external_lot_id: lotId }).pipe(
      map((response) => this.unwrap(response)),
      map((bag) => {
        if (bag === null) {
          this.rollback(lotId, previous);
          return { ok: false, state: this.stateOf(lotId) };
        }
        return { ok: true, state: this.stateOf(lotId) };
      }),
      catchError(() => {
        this.rollback(lotId, previous);
        return of({ ok: false, state: this.stateOf(lotId) });
      })
    );
  }

  /** This buyer's note on one lot, or `null` where they have not written one. */
  getNote(lotId: number): Observable<LotNote | null> {
    if (!this.isEnabled) {
      return of(null);
    }

    return this.http
      .getWithParams(`${NOTES_ROOT}/getHorseNoteByID`, {
        external_lot_id: lotId,
      })
      .pipe(
        map((response) => this.unwrap(response)),
        map((bag) => this.toNote(bag)),
        catchError(() => of(null))
      );
  }

  /**
   * Write the note. The whole note, every time — the panel owns the complete
   * value and the server replaces what it holds.
   *
   * Saving does NOT save the lot to the shortlist, matching what a note on one
   * of our own auctions does. The two are separate things a buyer attaches to a
   * horse, with a control each.
   */
  saveNote(
    lotId: number,
    note: {
      rating: string | null;
      notes: string;
      confirmation_type: string[];
      scope_type: string[];
    }
  ): Observable<LotMutationResult> {
    if (!this.isEnabled) {
      return of({ ok: false, state: null });
    }

    return this.http
      .post(`${NOTES_ROOT}/saveHorseNote`, {
        external_lot_id: lotId,
        rating: note.rating,
        notes: note.notes,
        confirmation_type: note.confirmation_type,
        scope_type: note.scope_type,
      })
      .pipe(
        map((response) => this.unwrap(response)),
        map((bag) => {
          if (bag === null) {
            return { ok: false, state: this.stateOf(lotId) };
          }

          const saved = this.toNote(bag);
          const previous = this.stateOf(lotId);

          this.merge([
            {
              lot_id: lotId,
              is_favourite: previous?.is_favourite ?? false,
              has_note: this.noteHasContent(saved),
              rating: (saved?.rating as LotState['rating']) ?? null,
            },
          ]);

          return { ok: true, state: this.stateOf(lotId), note: saved };
        }),
        catchError(() => of({ ok: false, state: this.stateOf(lotId) }))
      );
  }

  /** Delete the note. The lot stays on the shortlist if it was on it. */
  deleteNote(lotId: number): Observable<LotMutationResult> {
    if (!this.isEnabled) {
      return of({ ok: false, state: null });
    }

    return this.http
      .post(`${NOTES_ROOT}/deleteHorseNote`, { external_lot_id: lotId })
      .pipe(
        map((response) => this.unwrap(response)),
        map((bag) => {
          if (bag === null) {
            return { ok: false, state: this.stateOf(lotId) };
          }

          const previous = this.stateOf(lotId);
          this.merge([
            {
              lot_id: lotId,
              is_favourite: previous?.is_favourite ?? false,
              has_note: false,
              rating: null,
            },
          ]);

          return { ok: true, state: this.stateOf(lotId), note: null };
        }),
        catchError(() => of({ ok: false, state: this.stateOf(lotId) }))
      );
  }

  /* ---------------------------------------------------------------- *
   * Internals
   * ---------------------------------------------------------------- */

  /**
   * The platform's envelope, unwrapped — and its 200-status refusals caught.
   *
   * The base controller answers `{Error: [...]}` with a 200 status line, and
   * `IsBuyer` does the same. Reading only the status code would turn "you are
   * not a buyer" into "you have saved nothing", which is a lie the UI would
   * then repeat to the visitor. `null` here means refused OR malformed; every
   * caller treats it as "no answer".
   */
  private unwrap(response: any): Record<string, unknown> | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    // Capital `Error` from the base controller, lowercase from the unified
    // envelope. Both occur on the endpoints this service calls.
    if (response['Error'] || response['error'] || response['status'] === 'error') {
      return null;
    }

    const data = response['data'];

    return data && typeof data === 'object'
      ? (data as Record<string, unknown>)
      : null;
  }

  /**
   * A note, from either of the TWO shapes the platform answers with.
   *
   * `getHorseNoteByID` and `listAllHorseNotes` go through
   * `BuyerHorseNoteResource`, which nests the vocabularies as
   * `confirmation.types` / `scope.types`. `saveHorseNote` does not — it returns
   * the raw Eloquent model, so the same two lists arrive as
   * `note_confirmations[].confirmation_type` and
   * `note_scopes[].scope_type`.
   *
   * That inconsistency predates M19 and is not this feature's to fix — the
   * native notes panel reads the same two endpoints. It IS this method's to
   * absorb, so the rest of the client sees one shape: flat arrays, always
   * present, because the panel spreads them.
   */
  private toNote(bag: Record<string, unknown> | null): LotNote | null {
    if (!bag || bag['id'] === undefined || bag['id'] === null) {
      return null;
    }

    return {
      id: Number(bag['id']),
      lot_id: Number(bag['external_lot_id'] ?? 0),
      rating: (bag['rating'] as LotNote['rating']) ?? null,
      notes: String(bag['notes'] ?? ''),
      confirmation_type: this.vocabulary(
        bag,
        'confirmation',
        'note_confirmations',
        'confirmation_type'
      ),
      scope_type: this.vocabulary(bag, 'scope', 'note_scopes', 'scope_type'),
      updated_at: (bag['updated_at'] as string) ?? null,
    };
  }

  /**
   * One vocabulary list, from whichever of the two shapes carries it.
   *
   * @param nested  the resource key — `confirmation` / `scope`, holding `types`
   * @param raw     the model relation — `note_confirmations` / `note_scopes`
   * @param field   the column on each raw row
   */
  private vocabulary(
    bag: Record<string, unknown>,
    nested: string,
    raw: string,
    field: string
  ): string[] {
    const wrapped = bag[nested] as Record<string, unknown> | undefined;
    if (wrapped && Array.isArray(wrapped['types'])) {
      return wrapped['types'] as string[];
    }

    const rows = bag[raw];
    if (Array.isArray(rows)) {
      return rows
        .map((row) => (row as Record<string, unknown>)?.[field])
        .filter((value): value is string => typeof value === 'string');
    }

    return [];
  }

  private toState(row: Record<string, unknown>): LotState {
    return {
      lot_id: Number(row['external_lot_id']),
      is_favourite: !!row['is_favourite'],
      has_note: !!row['has_note'],
      rating: (row['rating'] as LotState['rating']) ?? null,
    };
  }

  /** A note row that holds nothing is not a note. Mirrors the server's rule. */
  private noteHasContent(note: LotNote | null): boolean {
    if (!note) {
      return false;
    }
    return (
      (note.notes ?? '').trim() !== '' ||
      !!note.rating ||
      (note.confirmation_type?.length ?? 0) > 0 ||
      (note.scope_type?.length ?? 0) > 0
    );
  }

  private merge(states: LotState[]): void {
    if (states.length === 0) {
      return;
    }

    // A NEW Map each time. Mutating the existing one would leave `OnPush`
    // components holding a reference that never changes identity, so nothing
    // would re-render.
    const next = new Map(this.statesSubject.value);
    states.forEach((state) => {
      next.set(state.lot_id, state);
      this.asked.add(state.lot_id);
    });
    this.statesSubject.next(next);
  }

  private rollback(lotId: number, previous: LotState | null): void {
    const next = new Map(this.statesSubject.value);
    if (previous) {
      next.set(lotId, previous);
    } else {
      next.delete(lotId);
      this.asked.delete(lotId);
    }
    this.statesSubject.next(next);
  }

  private reset(): void {
    this.asked.clear();
    this.statesSubject.next(new Map());
  }
}
